import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Sparkles, Rocket, ArrowRight, Code, Zap } from "lucide-react";
import Card from "@/components/Card";

interface CreateProjectCardProps {}

const CreateProjectCard: FC<CreateProjectCardProps> = () => {
  const navigate = useNavigate();

  const handleCreateProject = () => {
    navigate('/architecture');
  };

  return (
    <Card borderColor="transparent" bgColor="transparent" minHeight="[557px]">
      <button 
        className="relative overflow-hidden bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 rounded-3xl p-8 text-white w-full group transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-purple-400/50" 
        onClick={handleCreateProject}
        aria-label="Create new AI project"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-white/10 to-transparent rounded-full -translate-y-48 translate-x-48 group-hover:scale-110 transition-transform duration-700"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-cyan-400/20 to-transparent rounded-full translate-y-32 -translate-x-32 group-hover:scale-125 transition-transform duration-700"></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-gradient-to-r from-pink-400/30 to-purple-400/30 rounded-full -translate-x-16 -translate-y-16 group-hover:rotate-180 transition-transform duration-1000"></div>
        
        {/* Floating AI particles */}
        <div className="absolute top-20 left-20 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
        <div className="absolute top-32 right-32 w-1 h-1 bg-pink-400 rounded-full animate-ping"></div>
        <div className="absolute bottom-24 left-40 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce"></div>
        
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center">
          {/* Main icon with pulsing effect */}
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 group-hover:bg-white/20 transition-all duration-300">
              <Brain className="w-16 h-16 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          
          {/* Main heading with gradient text */}
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300 bg-clip-text text-transparent group-hover:from-cyan-200 group-hover:via-purple-200 group-hover:to-pink-200 transition-all duration-500">
            Create Your Backend Project
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl text-white/80 mb-8 max-w-2xl leading-relaxed">
            Build with state-of-the-art AI that designs your architecture, writes specs, and delivers production-ready code. Turn your idea into an advanced software in hours.
          </p>
          
          {/* Feature highlights */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Sparkles className="w-4 h-4 text-cyan-300" />
              <span className="text-sm font-medium">AI Architecture & Specs</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Code className="w-4 h-4 text-purple-300" />
              <span className="text-sm font-medium">Production Ready</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Zap className="w-4 h-4 text-pink-300" />
              <span className="text-sm font-medium">Automatic Maintenance</span>
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full blur opacity-70 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 px-8 py-4 rounded-full font-semibold text-white transition-all duration-300 flex items-center gap-3 shadow-xl hover:shadow-2xl transform hover:scale-105">
              <Rocket className="w-5 h-5" />
              Start Building For Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </div>
        </div>
      </button>
    </Card>
  );
};

export default CreateProjectCard;